import * as THREE from 'three';
import { pixelRatio2d } from '@/editor/math/const';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import { EDoorWindowType } from '@/editor/data/model/model';
import {
  getDoorArcOutline,
  getDoorWindowBgOutline,
  getDoorWindowSpecificOutline
} from '@/editor/data/model/doorWindowUtils';
import WindowModel from '@/editor/entities/2d/windowModel';
import {
  createLocalTransform,
  getLineBatch,
  pushClosedLoop,
  pushPolyline,
  pushSegment,
  type TLineBatches
} from './lineBatch';

/** 画一扇门窗需要的全部几何信息，两侧的数据（编辑器 IWindowData / 布局结果）各自适配到这个形状 */
export interface IDoorWindowDrawParams {
  type: EDoorWindowType;
  width: number;
  thickness: number;
  center: IPoint2d;
  rotation: number;
  flip?: boolean;
}

/** 材质由调用方注入，两边可以有不同的视觉语言 */
export interface IDoorWindowMaterials {
  /** 背景块与门窗扇 */
  bg: THREE.Material;
  /** 门扇之外的附加块，半透明 */
  other: THREE.Material;
  /** 深色轮廓：窗户外框、门扇轮廓 */
  outline: THREE.Material;
  /** 浅色轮廓：门的背景框与开启弧线 */
  bgOutline: THREE.Material;
  /** 落地窗中线用的虚线 */
  dashOutline: THREE.Material;
}

/**
 * 尺寸或位姿缺一个就会算出 NaN 几何体，进而让整个场景的包围盒失效、出空图。
 * 这里把坏数据挡在单个构件的粒度上，其余门窗照常绘制。
 */
const isFiniteDoorWindow = (params: IDoorWindowDrawParams) =>
  Number.isFinite(params.width) &&
  Number.isFinite(params.thickness) &&
  Number.isFinite(params.rotation) &&
  Number.isFinite(params.center?.x) &&
  Number.isFinite(params.center?.y);

const isWindowType = (type: EDoorWindowType) =>
  type === EDoorWindowType.window || type === EDoorWindowType.frenchWindow;

const isDoorType = (type: EDoorWindowType) =>
  type === EDoorWindowType.singleDoor || type === EDoorWindowType.doubleDoor || type === EDoorWindowType.motherDoor;

const KNOWN_DOOR_WINDOW_TYPES = new Set<string>(Object.values(EDoorWindowType));
const warnedUnknownTypes = new Set<string>();

/**
 * 门型取值不认识时按单开门画，与 generator.ts 的 `case singleDoor: default:` 一致。
 *
 * 不兜底的话后果是静默的：门窗扇的几何体（WindowModel 的 switch 是 default: break）和轮廓
 * 都不会产生，画面上只剩一个白色背景矩形——背景矩形只依赖 width/thickness，跟门型无关。
 * 布局结果里的 type 是后端给的字符串，对不上枚举取值时就会走到这里，所以要把实际值打出来。
 */
const normalizeDoorWindowType = (type: EDoorWindowType): EDoorWindowType => {
  if (KNOWN_DOOR_WINDOW_TYPES.has(type)) return type;

  const key = String(type);
  if (!warnedUnknownTypes.has(key)) {
    warnedUnknownTypes.add(key);
  }

  return EDoorWindowType.singleDoor;
};

/**
 * 落地窗中线是虚线。虚线相位在一个对象内是沿线段累计的，合并会让各扇窗的相位互相串台，
 * 所以只有它保持独立对象，挂在 Group 上跟着一起变换。
 */
const addFrenchWindowMiddleLine = (group: THREE.Group, width: number, material: THREE.Material) => {
  const halfWidth = width / 2 / pixelRatio2d;
  const z = WindowModel.subModelPositionZ.bgOutline;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([-halfWidth, 0, z, halfWidth, 0, z], 3));

  const line = new THREE.LineSegments(geometry, material);
  line.computeLineDistances();
  group.add(line);
};

/**
 * 背景与门窗扇。几何体直接复用 WindowModel 的静态构建方法，与画布上完全一致。
 * 这些 Mesh 各有自己的几何体，挂在一个带 T·R·S 的 Group 上。
 */
export const createDoorWindowMeshGroup = (params: IDoorWindowDrawParams, materials: IDoorWindowMaterials) => {
  const { width, thickness, type, center, rotation, flip } = params;
  const group = new THREE.Group();

  // z 偏移沿用 WindowModel.subModelPositionZ
  const bgMesh = new THREE.Mesh(WindowModel.createDoorWindowBgGeometry(width, thickness), materials.bg);
  bgMesh.position.z = WindowModel.subModelPositionZ.bg;
  group.add(bgMesh);

  const { otherGeometry, fanGeometry } = WindowModel.createDoorWindowSpecificGeometry(type, width);
  if (otherGeometry) {
    const otherMesh = new THREE.Mesh(otherGeometry, materials.other);
    otherMesh.position.z = WindowModel.subModelPositionZ.other;
    group.add(otherMesh);
  }
  if (fanGeometry) {
    const fanMesh = new THREE.Mesh(fanGeometry, materials.bg);
    fanMesh.position.z = WindowModel.subModelPositionZ.fan;
    group.add(fanMesh);
  }

  if (type === EDoorWindowType.frenchWindow) {
    addFrenchWindowMiddleLine(group, width, materials.dashOutline);
  }

  // live 用 mirrorObject 把每个子 Mesh 的几何体按局部 x 取反，
  // 这里直接给整组取反：矩阵是 T·R·S，合成顺序一致
  if (flip) group.scale.x = -1;

  group.position.set(center.x / pixelRatio2d, center.y / pixelRatio2d, ENTITY_RENDER_ORDER.doorWindowBg);
  group.rotation.z = rotation;

  return group;
};

/**
 * 轮廓合并进全局批次，所以要自己把局部坐标变换到世界坐标（与 Group 的 T·R·S 等价），
 * 并把 Group 与子模型的 z 相加烧进顶点。材质与 z 分层对应 WindowModel.createModelOutline 的常态取色。
 */
export const pushDoorWindowOutline = (
  batches: TLineBatches,
  params: IDoorWindowDrawParams,
  materials: IDoorWindowMaterials
) => {
  const { width, thickness, type, center, rotation, flip } = params;
  const transform = createLocalTransform(center, rotation, flip);
  const baseZ = ENTITY_RENDER_ORDER.doorWindowBg;
  const bgOutline = getDoorWindowBgOutline(width, thickness);

  if (isWindowType(type)) {
    const positions = getLineBatch(batches, materials.outline);
    const z = baseZ + WindowModel.subModelPositionZ.bgOutline;
    pushClosedLoop(positions, bgOutline, z, transform);

    // 中线：窗户实线可以合并，落地窗虚线由 createDoorWindowMeshGroup 单独出对象
    if (type === EDoorWindowType.window) {
      pushSegment(positions, { x: -width / 2, y: 0 }, { x: width / 2, y: 0 }, z, transform);
    }
    return;
  }

  if (!isDoorType(type)) return;

  const bgOutlinePositions = getLineBatch(batches, materials.bgOutline);
  const outlineZ = baseZ + WindowModel.subModelPositionZ.outline;
  pushClosedLoop(bgOutlinePositions, bgOutline, outlineZ, transform);

  for (const outline of getDoorArcOutline(type, width)) {
    pushPolyline(bgOutlinePositions, outline, outlineZ, transform);
  }

  const fanOutlinePositions = getLineBatch(batches, materials.outline);
  const fanOutlineZ = baseZ + WindowModel.subModelPositionZ.fanOutline;
  for (const outline of getDoorWindowSpecificOutline(type, width)) {
    pushClosedLoop(fanOutlinePositions, outline, fanOutlineZ, transform);
  }
};

/** 一扇门窗的完整绘制：填充挂到 scene，轮廓进批次。数据不合法时只跳过这一扇 */
export const addDoorWindow = (
  scene: THREE.Scene,
  batches: TLineBatches,
  params: IDoorWindowDrawParams,
  materials: IDoorWindowMaterials
) => {
  if (!isFiniteDoorWindow(params)) {
    console.warn('异常门窗数据，已跳过', params);
    return;
  }

  // 归一化后再往下传，填充与轮廓两边看到的门型必须一致
  const normalized = { ...params, type: normalizeDoorWindowType(params.type) };

  scene.add(createDoorWindowMeshGroup(normalized, materials));
  pushDoorWindowOutline(batches, normalized, materials);
};
